import React, { useEffect, useState } from 'react';
import ClosedSidebar from '../../components/closedSidebar';
import OpenedSidebar from '../../components/openedSidebar';
import Pagination from '../../components/Pagination';
import EmptyState from '../../components/EmptyState';
import CreateTentModal from '../../components/modals/AddTentModal';
import { useTranslation } from 'react-i18next';
import { tentActions } from '../../redux-utils/actions/tent-actions';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../custom-hooks/useAuth';
import { useTentStatus } from '../../custom-hooks/useTentStatus';
import EditTentModal from '../../components/modals/EditTentModal';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import Loader from '../../components/Loader';
import { useNavigate } from 'react-router';

const Card = ({
    name,
    capacity,
    staffs,
    type,
    participants,
    status,
    created_at,
    id,
    triggerEditModal,
    triggerDelete,
    triggerAssignment,
}) => {
    const { t } = useTranslation();

    const tent = { capacity, staffs, participants };
    const statusObj = useTentStatus(tent);
    const formattedDate = new Date(created_at).toLocaleDateString();

    const statusStyles = {
        problematic: 'bg-red-100 text-red-600 border border-red-400',
        okay: 'bg-green-100 text-green-600 border border-green-400',
    };

    const supervisor = staffs.find((staff) => staff.role === 'supervisor');
    const supervisorName = supervisor
        ? `${supervisor.first_name} ${supervisor.last_name}`
        : 'No supervisor assigned';

    return (
        <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-xl transition-shadow duration-300">
            <div className="flex justify-between items-start border-b pb-4 mb-4">
                <div>
                    <h1 className="text-xl font-bold text-gray-800">{name}</h1>
                    <p className="text-sm text-gray-500">
                        {t('tent-management-page.Created')}: {formattedDate}
                    </p>
                </div>
                <span
                    className={`text-xs font-medium px-3 py-1 rounded-full ${
                        statusStyles[statusObj.status] ||
                        'bg-gray-200 text-gray-700'
                    }`}
                    title={statusObj.reason || ''}
                >
                    {statusObj.status === 'PROBLEMATIC'
                        ? t('tent-management-page.Problematic')
                        : t('tent-management-page.Okay')}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm text-gray-700 mb-4">
                <div>
                    <span className="font-semibold text-gray-500">
                        {t('tent-management-page.Capacity')}:
                    </span>
                    {capacity}
                </div>
                <div>
                    <span className="font-semibold text-gray-500">
                        {' '}
                        {t('tent-management-page.Type')}:
                    </span>{' '}
                    <span
                        className={
                            type === 'male' ? 'text-blue-600' : 'text-pink-600'
                        }
                    >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                    </span>
                </div>
                <div className="col-span-2">
                    <span className="font-semibold text-gray-500">
                        {t('tent-management-page.Supervisor')}:
                    </span>
                    {supervisorName}
                </div>
            </div>

            <div className="flex justify-between items-center gap-3 mt-4">
                <button
                    onClick={() => triggerAssignment(id)}
                    className="bg-primary hover:bg-primary text-white text-sm font-medium py-2 px-4 rounded-full"
                >
                    {t('tent-management-page.Assign')}
                </button>

                <button
                    onClick={() => triggerEditModal(id)}
                    className="bg-white  border border-primary text-primary text-sm font-medium py-2 px-4 rounded-full"
                >
                    {t('tent-management-page.Edit')}
                </button>
                <button
                    onClick={() => triggerDelete(id)}
                    className="bg-white  border border-primary text-primary text-sm font-medium py-2 px-4 rounded-full"
                >
                    {t('tent-management-page.Delete')}
                </button>
            </div>
        </div>
    );
};

function Tents() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { allTentData, status, error } = useSelector(
        (state) => state.tentState
    );
    const ReactSwal = withReactContent(Swal);
    const [isOpened, setIsOpened] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const { allTentAction, tentDeleteAction, resetTentAction } = tentActions;
    const [selectedTent, setSelectedTent] = useState(null);
    const { getLogin } = useAuth();
    const loginStorage = JSON.parse(getLogin());
    const [searchQuery, setSearchQuery] = useState('');

    const { t } = useTranslation();
    const data = allTentData || [];

    const handlePerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleCreateModalClose = () => {
        setShowCreateModal(false);
    };

    const handleEditModalClose = () => {
        setShowEditModal(false);
    };

    const filteredData = data.filter((tent) =>
        tent.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = filteredData.slice(startIndex, endIndex);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 800,
        bgcolor: 'background.paper',
        borderRadius: '20px',
        boxShadow: 24,
        outline: '0',
        p: 4,
        zIndex: 5,
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const toggleSidebar = () => {
        setIsOpened(!isOpened);
    };

    const onTriggerEditHandler = (id) => {
        setSelectedTent(data.find((tent) => tent.id === id));
        setShowEditModal(true);
    };

    const onTriggerDeleteHandler = (id) => {
        ReactSwal.fire({
            title: t('tent-management-page.Are you sure?'),
            text: t(
                'tent-management-page.This tent will be permanently deleted'
            ),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('tent-management-page.Yes, delete it!'),
        }).then((result) => {
            if (result.isConfirmed) {
                const params = {
                    id: id,
                    token: loginStorage && loginStorage.loginData.data.token,
                };
                dispatch(tentDeleteAction(params));
            }
        });
    };

    const onTriggerAssignment = (id) => {
        navigate(`/participant-assignment/${id}`);
    };

    useEffect(() => {
        const params = {
            token: loginStorage && loginStorage.loginData.data.token,
        };

        if (data.length === 0) {
            dispatch(allTentAction(params));
        }
    }, []);

    useEffect(() => {
        if (status === 'tent_delete_success') {
            ReactSwal.fire({
                title: t('tent-management-page.Success!'),
                text: t('tent-management-page.Tent deleted successfully'),
                icon: 'success',
            }).then(() => {
                dispatch(resetTentAction());
            });
        }

        if (status === 'tent_create_failed') {
            const errorMessage =
                error?.message || t('tent-management-page.Tent delete failed');

            ReactSwal.fire({
                title: t('tent-management-page.Error!'),
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'OK',
            }).then(() => {
                dispatch(resetTentAction());
            });
        }
    }, [status, ReactSwal, dispatch, resetTentAction, error, t]);

    return (
        <main className=" font-sans flex">
            {isOpened ? (
                <OpenedSidebar toggleSidebar={toggleSidebar} />
            ) : (
                <ClosedSidebar toggleSidebar={toggleSidebar} />
            )}

            {status === 'tent_delete_start' ||
                (status === 'all_tent_fetch_start' && <Loader />)}

            {showCreateModal && (
                <CreateTentModal
                    style={style}
                    open={showCreateModal}
                    handleClose={handleCreateModalClose}
                />
            )}

            {showEditModal && (
                <EditTentModal
                    style={style}
                    open={showEditModal}
                    handleClose={handleEditModalClose}
                    selectedTent={selectedTent}
                />
            )}

            <section className="w-full bg-[#F9F9F9] px-20 pt-10">
                <h1 className="text-2xl text-tertiary font-medium">
                    {t('tent-management-page.Tents')}
                </h1>
                <div className="w-full h-20 bg-white mt-10 rounded-lg flex items-center justify-between px-5">
                    <div className="relative w-[30%]">
                        <svg
                            className="absolute right-5 top-3"
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="15"
                            viewBox="0 0 20 20"
                            fill="none"
                        >
                            <path
                                d="M14.1668 14.1667L17.5001 17.5M2.50012 9.16667C2.50012 10.9348 3.2025 12.6305 4.45274 13.8807C5.70299 15.131 7.39868 15.8333 9.16679 15.8333C10.9349 15.8333 12.6306 15.131 13.8808 13.8807C15.1311 12.6305 15.8335 10.9348 15.8335 9.16667C15.8335 7.39856 15.1311 5.70286 13.8808 4.45262C12.6306 3.20238 10.9349 2.5 9.16679 2.5C7.39868 2.5 5.70299 3.20238 4.45274 4.45262C3.2025 5.70286 2.50012 7.39856 2.50012 9.16667Z"
                                stroke="#2F4858"
                                stroke-opacity="0.6"
                                stroke-width="1.25"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={`${t('tent-management-page.Search')}`}
                            className="outline-primary text-secondary bg-[#FFFFFF] border-2 border-[#EFEFEF] h-10 pl-3 rounded-full w-full"
                        />
                    </div>
                    <button
                        className="text-white bg-primary rounded-full w-48 font-medium h-10"
                        onClick={() => setShowCreateModal(true)}
                    >
                        {t('tent-management-page.Create Tent')}
                    </button>
                </div>
                {data && data.length > 0 ? (
                    <>
                        <div className="mt-10 grid grid-cols-3 auto-rows-fr gap-4 ">
                            {currentData.map((dat, index) => (
                                <Card
                                    key={index}
                                    {...dat}
                                    triggerEditModal={onTriggerEditHandler}
                                    triggerDelete={onTriggerDeleteHandler}
                                    triggerAssignment={onTriggerAssignment}
                                />
                            ))}
                        </div>
                        <Pagination
                            handlePageChange={handlePageChange}
                            totalPages={totalPages}
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            handlePerPageChange={handlePerPageChange}
                        />
                    </>
                ) : (
                    <EmptyState />
                )}
            </section>
        </main>
    );
}

export default Tents;
